import { generateInfoPageRoutes, getInfoPageUaRuRouteNames } from '@/feature/info-pages/routes';

/**
 * Wrap a dynamic import with retry logic and reload fallback.
 * Handles chunk load failures caused by stale caches after deploy
 * (especially WKWebView in the mobile app).
 */
function lazyLoad(importFn, retries = 2, interval = 1500) {
  return async () => {
    try {
      return await importFn();
    } catch (error) {
      if (retries > 0) {
        await new Promise(resolve => {
          setTimeout(resolve, interval);
        });
        return lazyLoad(importFn, retries - 1, interval)();
      }
      // All retries exhausted — attempt reload
      const lastReload = sessionStorage.getItem('chunk-reload');
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem('chunk-reload', String(now));
        window.location.reload();
        return new Promise(() => {});
      }
      // Reload already attempted — throw, router.onError will report to Sentry
      throw error;
    }
  };
}

export const uaRuRouteNames = [
  'termsAndConditions',
  'personalDataProcessing',
  'renewPassword',
  'verifyEmail',
  'login',
  'socialLogin',
  'webLogin',
  'lostPassword',
  'registration',
  'dashboard',
  'dashboardLock',
  'accountSettings',
  'questions',
  'questionDetail',
  'appointments',
  'appointmentDetail',
  'profileDialog',
  'authorization',
  'cookies',
  'thematicCounsellingCookiesUA',
  'thematicCounsellingTermsAndConditionsUA',
  'thematicCounsellingPersonalDataProcessingUA',
  'thematicCounsellingUA',
  'thematicCounselling',
  'upgrade',
  'protectionOfWhistleblowers',
  'benefitManagement',
  ...getInfoPageUaRuRouteNames()
];

export const routes = {
  index: {
    paths: {
      cs: '/',
      sk: '/',
      en: '/',
      hu: '/'
    },
    component: lazyLoad(() => import('@/pages/homepage/index.vue')),
    name: 'index'
  },
  healthboxLanding: {
    paths: {
      cs: '/healthbox'
    },
    component: lazyLoad(() => import('@/pages/healthbox/index.vue')),
    name: 'healthboxLanding'
  },
  contactPage: {
    paths: {
      cs: '/kontakt'
    },
    component: lazyLoad(() => import('@/pages/contact/index.vue')),
    name: 'contactPage'
  },
  careerPage: {
    paths: {
      cs: '/career',
      sk: '/career',
      en: '/career'
    },
    component: lazyLoad(() => import('@/feature/career-page/pages/index.vue')),
    name: 'careerPage'
  },
  careerPageDetail: {
    paths: {
      cs: '/career/:slug',
      sk: '/career/:slug',
      en: '/career/:slug'
    },
    component: lazyLoad(() => import('@/feature/career-page/pages/detail.vue')),
    name: 'careerPageDetail'
  },
  servicePage: {
    paths: {
      cs: '/service',
      en: '/service',
      sk: '/service',
      hu: '/service'
    },
    component: lazyLoad(() => import('~/pages/service/index.vue')),
    name: 'servicePage'
  },
  hrbHealth: {
    paths: {
      cs: '/zdravi',
      sk: '/zdravie',
      en: '/health'
    },
    component: lazyLoad(() => import('@/pages/hrbHealth.vue')),
    name: 'hrbHealth'
  },
  cookies: {
    paths: {
      cs: '/cookies',
      sk: '/cookies',
      en: '/cookies',
      uk: '/cookies',
      ru: '/cookies',
      hu: '/cookies'
    },
    component: lazyLoad(() => import('@/pages/cookies.vue')),
    name: 'cookies'
  },
  termsAndConditions: {
    paths: {
      cs: '/podminky-uziti',
      sk: '/podmienky-pouzivania',
      en: '/terms-of-use',
      uk: '/umovy-korystuvannya',
      ru: '/usloviya-ekspluatatsii',
      hu: '/terms-of-use'
    },
    component: lazyLoad(() => import('@/pages/terms-and-conditions.vue')),
    name: 'termsAndConditions'
  },
  termsAndConditionsAi: {
    paths: {
      cs: '/podminky-uziti-ai'
    },
    component: lazyLoad(() => import('@/pages/terms-and-conditions.vue')),
    name: 'termsAndConditionsAi'
  },
  vitalSigns: {
    paths: {
      cs: '/dashboard/vital-signs'
    },
    component: lazyLoad(() => import('@/feature/vital-signs/pages/index.vue')),
    name: 'vitalSigns'
  },
  vitalSignsPublic: {
    paths: { cs: '/vital-signs-public' },
    component: lazyLoad(() => import('@/feature/vital-signs/pages/public.vue')),
    name: 'vitalSignsPublic'
  },
  personalDataProcessing: {
    paths: {
      cs: '/zasady-zpracovani-osobnich-udaju',
      sk: '/zasady-spracovania-osobnych-udajov',
      en: '/personal-data-processing',
      hu: '/personal-data-processing',
      uk: '/pryntsypy-obrobky-personalnykh-danykh',
      ru: '/printsipy-obrabotki-personalnykh-dannykh'
    },
    component: lazyLoad(() => import('@/pages/personal-data-processing.vue')),
    name: 'personalDataProcessing'
  },
  ...generateInfoPageRoutes(lazyLoad),
  FAQ: {
    paths: {
      cs: '/caste-dotazy',
      sk: '/caste-dotazy',
      en: '/faq',
      hu: '/faq'
    },
    component: lazyLoad(() => import('@/pages/faq/index.vue')),
    name: 'FAQ'
  },
  rsAssistant: {
    paths: {
      cs: '/rs-asistent'
    },
    component: lazyLoad(() => import('@/pages/rs-asistant/index.vue')),
    name: 'rsAssistant'
  },
  astmaHelp: {
    paths: {
      cs: '/astmaHelp'
    },
    component: lazyLoad(() => import('@/pages/astmaHelp/index.vue')),
    name: 'astmaHelp'
  },
  conferenceIndex: {
    paths: {
      cs: '/konference'
    },
    component: lazyLoad(() => import('@/feature/conference/pages/index.vue')),
    name: 'conferenceIndex'
  },
  evaluation: {
    paths: {
      cs: '/hodnoceni/objednavka',
      en: '/evaluation/appointment',
      hu: '/evaluation/appointment'
    },
    component: lazyLoad(() => import('@/pages/evaluation/order/index.vue')),
    name: 'evaluation'
  },
  renewPassword: {
    paths: {
      cs: '/obnoveni-hesla',
      sk: '/obnovenie-hesla',
      en: '/renew-password',
      hu: '/renew-password',
      uk: '/skydannya-parolya',
      ru: '/vosstanovleniye-parolya'
    },
    component: lazyLoad(() => import('@/pages/renew-password/index.vue')),
    name: 'renewPassword'
  },
  verifyEmail: {
    paths: {
      cs: '/overeni-emailu',
      sk: '/overenie-emailu',
      en: '/email-verification',
      hu: '/email-verification',
      uk: '/pidtverdzhennya-elektronnoyi-poshty',
      ru: '/podtverzhdeniye-po-eletronnoy-pochte'
    },
    component: lazyLoad(() => import('@/pages/email-verification/index.vue')),
    name: 'verifyEmail'
  },
  // thematicCounsellingCookiesUA: {
  //   paths: {
  //     uk: '/poradna/dlya-ukrayiny/cookies',
  //     ru: '/poradna/dlya-ukrainy/cookies'
  //   },
  //   component: () => import('@/pages/cookies.vue'),
  //   name: 'thematicCounsellingCookiesUA'
  // },
  // thematicCounsellingTermsAndConditionsUA: {
  //   paths: {
  //     uk: '/poradna/dlya-ukrayiny/umovy-vykorystannya',
  //     ru: '/poradna/dlya-ukrainy/usloviya-ispolzovaniya'
  //   },
  //   component: () => import('@/pages/terms-and-conditions.vue'),
  //   name: 'thematicCounsellingTermsAndConditionsUA'
  // },
  // thematicCounsellingPersonalDataProcessingUA: {
  //   paths: {
  //     uk: '/poradna/dlya-ukrayiny/pryntsypy-obrobky-personalnykh-danykh',
  //     ru: '/poradna/dlya-ukrainy/printsipy-obrabotki-personalnykh-dannykh'
  //   },
  //   component: () => import('@/pages/personal-data-processing.vue'),
  //   name: 'thematicCounsellingPersonalDataProcessingUA'
  // },
  // thematicCounsellingUA: {
  //   paths: {
  //     uk: '/poradna/dlya-ukrayiny',
  //     ru: '/poradna/dlya-ukrainy'
  //   },
  //   component: () => import('@/pages/counselling/_thematicCounselling/index.vue'),
  //   name: 'thematicCounsellingUA'
  // },
  // thematicCounsellingCookies: {
  //   paths: {
  //     cs: '/poradna/:thematicCounselling/cookies'
  //   },
  //   component: () => import('@/pages/cookies.vue'),
  //   name: 'thematicCounsellingCookies'
  // },
  thematicCounsellingTermsAndConditions: {
    paths: {
      cs: '/poradna/:thematicCounselling/podminky-uziti'
    },
    component: lazyLoad(() => import('@/pages/terms-and-conditions.vue')),
    name: 'thematicCounsellingTermsAndConditions'
  },
  thematicCounsellingPersonalDataProcessing: {
    paths: {
      cs: '/poradna/:thematicCounselling/zasady-zpracovani-osobnich-udaju'
    },
    component: lazyLoad(() => import('@/pages/personal-data-processing.vue')),
    name: 'thematicCounsellingPersonalDataProcessing'
  },
  thematicCounselling: {
    paths: {
      cs: '/poradna/:thematicCounselling',
      uk: '/poradna/:thematicCounselling',
      ru: '/poradna/:thematicCounselling'
    },
    component: lazyLoad(() => import('@/pages/counselling/_thematicCounselling/index.vue')),
    name: 'thematicCounselling'
  },
  upgrade: {
    paths: {
      cs: '/aktualizace',
      sk: '/aktualizacia',
      en: '/upgrade',
      hu: '/upgrade',
      uk: '/upgrade',
      ru: '/upgrade'
    },
    component: lazyLoad(() => import('@/pages/upgrade.vue')),
    name: 'upgrade'
  },
  login: {
    paths: {
      cs: '/login',
      sk: '/login',
      en: '/login',
      hu: '/login',
      uk: '/login',
      ru: '/login'
    },
    component: lazyLoad(() => import('@/pages/login/index.vue')),
    name: 'login'
  },
  socialLogin: {
    paths: {
      cs: '/login/social-login',
      sk: '/login/social-login',
      en: '/login/social-login',
      hu: '/login/social-login',
      uk: '/login/social-login',
      ru: '/login/social-login'
    },
    component: lazyLoad(() => import('@/pages/login/social-login/index.vue')),
    name: 'socialLogin'
  },
  webLogin: {
    paths: {
      cs: '/web-login',
      sk: '/web-login',
      en: '/web-login',
      hu: '/web-login',
      uk: '/web-login',
      ru: '/web-login'
    },
    component: lazyLoad(() => import('@/pages/login/index.vue')),
    name: 'webLogin'
  },
  lostPassword: {
    paths: {
      cs: '/zapomenute-heslo',
      sk: '/zabudnute-heslo',
      en: '/lost-password',
      hu: '/lost-password',
      uk: '/zabutyy-parol',
      ru: '/zabytyy-parol'
    },
    component: lazyLoad(() => import('@/pages/lost-password/index.vue')),
    name: 'lostPassword'
  },
  registration: {
    paths: {
      cs: '/registrace',
      sk: '/registracia',
      en: '/registration',
      hu: '/registration',
      uk: '/reyestratsiya',
      ru: '/registratsiya'
    },
    component: lazyLoad(() => import('@/pages/registration/index.vue')),
    name: 'registration'
  },
  dashboard: {
    paths: {
      cs: '/dashboard',
      sk: '/dashboard',
      en: '/dashboard',
      hu: '/dashboard',
      uk: '/dashboard',
      ru: '/dashboard'
    },
    component: lazyLoad(() => import('@/pages/dashboard/index.vue')),
    name: 'dashboard'
  },
  dashboardLock: {
    paths: {
      cs: '/dashboard/lock',
      sk: '/dashboard/lock',
      en: '/dashboard/lock',
      hu: '/dashboard/lock',
      uk: '/dashboard/lock',
      ru: '/dashboard/lock'
    },
    component: lazyLoad(() => import('@/pages/dashboard/lock/index.vue')),
    name: 'dashboardLock'
  },
  accountSettings: {
    paths: {
      cs: '/dashboard/profil',
      sk: '/dashboard/profil',
      en: '/dashboard/profile',
      hu: '/dashboard/profile',
      uk: '/dashboard/profil',
      ru: '/dashboard/profil'
    },
    component: lazyLoad(() => import('@/pages/dashboard/profile/index.vue')),
    name: 'accountSettings'
  },
  benefitManagement: {
    paths: {
      cs: '/dashboard/profile/benefit',
      sk: '/dashboard/profile/benefit',
      en: '/dashboard/profile/benefit',
      hu: '/dashboard/profile/benefit',
      uk: '/dashboard/profile/benefit',
      ru: '/dashboard/profile/benefit'
    },
    component: lazyLoad(() => import('~/pages/dashboard/profile/benefit.vue')),
    name: 'benefitManagement'
  },
  questions: {
    paths: {
      cs: '/dashboard/questions',
      sk: '/dashboard/questions',
      en: '/dashboard/questions',
      hu: '/dashboard/questions',
      uk: '/dashboard/questions',
      ru: '/dashboard/questions'
    },
    component: lazyLoad(() => import('@/feature/questions/pages/index.vue')),
    name: 'questions'
  },
  questionDetail: {
    paths: {
      cs: '/dashboard/questions/:id',
      sk: '/dashboard/questions/:id',
      en: '/dashboard/questions/:id',
      hu: '/dashboard/questions/:id',
      uk: '/dashboard/questions/:id',
      ru: '/dashboard/questions/:id'
    },
    component: lazyLoad(() => import('@/feature/questions/pages/detail.vue')),
    name: 'questionDetail'
  },
  appointments: {
    paths: {
      cs: '/dashboard/appointments',
      sk: '/dashboard/appointments',
      en: '/dashboard/appointments',
      hu: '/dashboard/appointments',
      uk: '/dashboard/appointments',
      ru: '/dashboard/appointments'
    },
    component: lazyLoad(() => import('@/feature/orders/pages/index.vue')),
    name: 'appointments'
  },
  appointmentDetail: {
    paths: {
      cs: '/dashboard/appointments/:id',
      sk: '/dashboard/appointments/:id',
      en: '/dashboard/appointments/:id',
      hu: '/dashboard/appointments/:id',
      uk: '/dashboard/appointments/:id',
      ru: '/dashboard/appointments/:id'
    },
    component: lazyLoad(() => import('@/feature/orders/pages/detail.vue')),
    name: 'appointmentDetail'
  },
  prevention: {
    paths: {
      cs: '/dashboard/prevention',
      sk: '/dashboard/prevention',
      en: '/dashboard/prevention',
      hu: '/dashboard/prevention'
    },
    component: lazyLoad(() => import('@/pages/dashboard/prevention/index.vue')),
    name: 'prevention'
  },
  preventionList: {
    paths: {
      cs: '/dashboard/prevention/list',
      sk: '/dashboard/prevention/list',
      en: '/dashboard/prevention/list',
      hu: '/dashboard/prevention/list'
    },
    component: lazyLoad(() => import('@/pages/dashboard/prevention/list/index.vue')),
    name: 'preventionList'
  },
  medicalProcedure: {
    paths: {
      cs: '/dashboard/prevention/medical-procedure',
      sk: '/dashboard/prevention/medical-procedure',
      en: '/dashboard/prevention/medical-procedure',
      hu: '/dashboard/prevention/medical-procedure'
    },
    component: lazyLoad(() => import('@/pages/dashboard/prevention/medical-procedure/index.vue')),
    name: 'medicalProcedure'
  },
  profileExplore: {
    paths: {
      cs: '/dashboard/explore',
      sk: '/dashboard/explore',
      en: '/dashboard/explore',
      hu: '/dashboard/explore'
    },
    component: lazyLoad(() => import('@/pages/dashboard/explore/index.vue')),
    name: 'profileExplore'
  },
  profileExploreDetail: {
    paths: {
      cs: '/dashboard/explore/:type',
      sk: '/dashboard/explore/:type',
      en: '/dashboard/explore/:type',
      hu: '/dashboard/explore/:type'
    },
    component: lazyLoad(() => import('@/pages/dashboard/explore/detail/index.vue')),
    name: 'profileExploreDetail'
  },
  profileDialog: {
    paths: {
      cs: '/dashboard/dialog',
      sk: '/dashboard/dialog',
      en: '/dashboard/dialog',
      hu: '/dashboard/dialog',
      uk: '/dashboard/dialog',
      ru: '/dashboard/dialog'
    },
    component: lazyLoad(() => import('@/pages/dashboard/dialog/index.vue')),
    name: 'profileDialog'
  },
  aeskuLab: {
    paths: {
      cs: '/ulhc/aeskulab',
      sk: '/ulhc/unilabs'
    },
    component: lazyLoad(() => import('@/pages/aeskulab/index.vue')),
    name: 'aeskuLab'
  },
  aeskuLabPersonalDataProcessing: {
    paths: {
      cs: '/ulhc/aeskulab/zasady-zpracovani-osobnich-udaju',
      sk: '/ulhc/unilabs/zasady-spracovania-osobnych-udajov'
    },
    component: lazyLoad(() => import('@/pages/personal-data-processing.vue')),
    name: 'aeskuLabPersonalDataProcessing'
  },
  aeskuLabTermsAndConditions: {
    paths: {
      cs: '/ulhc/aeskulab/podminky-uziti',
      sk: '/ulhc/unilabs/podmienky-pouzivania'
    },
    component: lazyLoad(() => import('@/pages/terms-and-conditions.vue')),
    name: 'aeskuLabTermsAndConditions'
  },
  aeskuLabCookies: {
    paths: {
      cs: '/ulhc/aeskulab/cookies',
      sk: '/ulhc/unilabs/cookies'
    },
    component: lazyLoad(() => import('@/pages/cookies.vue')),
    name: 'aeskuLabCookies'
  },
  authorization: {
    paths: {
      cs: '/autorizace',
      sk: '/autorizacia',
      en: '/authorization',
      hu: '/authorization',
      uk: '/avtoryzatsiya',
      ru: '/avtorizatsiya'
    },
    component: lazyLoad(() => import('@/pages/authorization/index.vue')),
    name: 'authorization'
  },
  articles: {
    paths: {
      cs: '/:partnerUrl/clanky/:slug',
      sk: '/:partnerUrl/clanky/:slug'
    },
    component: lazyLoad(() => import('@/pages/_slug/articles/_article-slug/index.vue')),
    name: 'hrbArticles'
  },
  hrbCookies: {
    paths: {
      cs: '/:partnerUrl/cookies',
      sk: '/:partnerUrl/cookies',
      en: '/:partnerUrl/cookies'
    },
    component: lazyLoad(() => import('@/pages/cookies.vue')),
    name: 'hrbCookies'
  },
  hrbTermsAndConditions: {
    paths: {
      cs: '/:partnerUrl/podminky-uziti',
      sk: '/:partnerUrl/podmienky-pouzivania',
      en: '/:partnerUrl/terms-of-use'
    },
    component: lazyLoad(() => import('@/pages/terms-and-conditions.vue')),
    name: 'hrbTermsAndConditions'
  },
  hrbPersonalDataProcessing: {
    paths: {
      cs: '/:partnerUrl/zasady-zpracovani-osobnich-udaju',
      sk: '/:partnerUrl/zasady-spracovania-osobnych-udajov',
      en: '/:partnerUrl/personal-data-processing'
    },
    component: lazyLoad(() => import('@/pages/personal-data-processing.vue')),
    name: 'hrbPersonalDataProcessing'
  },
  protectionOfWhistleblowers: {
    paths: {
      cs: '/ochrana-oznamovatelu',
      sk: '/ochrana-oznamovatelov',
      en: '/protection-of-whistleblowers',
      uk: '/zachist-vykryvaciv',
      ru: '/zashchita-razoblachiteley',
      hu: '/protection-of-whistleblowers'
    },
    component: lazyLoad(() => import('@/pages/protection-of-whistleblowers.vue')),
    name: 'protectionOfWhistleblowers'
  },
  regionSelector: {
    paths: {
      cs: '/region',
      sk: '/region',
      en: '/region',
      uk: '/region',
      ru: '/region',
      hu: '/region'
    },
    component: lazyLoad(() => import('~/pages/region-selector.vue')),
    name: 'regionSelector'
  },
  security: {
    paths: {
      cs: '/security',
      sk: '/security',
      en: '/security',
      uk: '/security',
      ru: '/secuirty',
      hu: '/security'
    },
    component: lazyLoad(() => import('@/pages/security.vue')),
    name: 'security'
  }
};
